import { css, html, LitElement } from "lit";
import { customElement, property, state } from "lit/decorators.js";

/**
 * Standalone performance HUD. Measures FPS via `requestAnimationFrame` deltas
 * collected in a small ring buffer. Throttles DOM writes to keep itself
 * essentially free in the critical path.
 *
 * Usage:
 *   <sugar-fps-monitor></sugar-fps-monitor>
 *   <sugar-fps-monitor position="top-left" update-interval="250" detailed></sugar-fps-monitor>
 *
 * Attributes:
 *   - `position`: "top-right" (default) | "top-left" | "bottom-right" |
 *     "bottom-left" | "none". `none` strips the built-in fixed positioning
 *     so the host document can place the element freely.
 *   - `update-interval`: DOM refresh throttle in ms (default 500). Sampling
 *     still happens every frame; only the visual write is rate-limited.
 *   - `detailed`: when present, also renders frame time and (if an Engine
 *     instance is live) draw call / triangle counts exposed by
 *     `Engine.getLastWebGLMainFrameStats()`.
 *
 * Read-only public props: `fps`, `frameMs`.
 * Public method: `reset()` clears the sample ring buffer.
 */
@customElement("sugar-fps-monitor")
export class SugarFpsMonitor extends LitElement {
  @property({ type: String }) position:
    | "top-right"
    | "top-left"
    | "bottom-right"
    | "bottom-left"
    | "none" = "top-right";

  @property({ type: Number, attribute: "update-interval" })
  updateInterval = 500;

  @property({ type: Boolean }) detailed = false;

  @state() private _fps = 0;
  @state() private _frameMs = 0;
  @state() private _calls = 0;
  @state() private _triangles = 0;

  private _rafId: number | null = null;
  private _lastFrameTime = 0;
  private _lastDisplayTime = 0;
  private _samples: number[] = [];
  private _sampleCap = 120;

  get fps(): number {
    return this._fps;
  }
  get frameMs(): number {
    return this._frameMs;
  }

  public reset(): void {
    this._samples.length = 0;
    this._lastFrameTime = 0;
    this._lastDisplayTime = 0;
    this._fps = 0;
    this._frameMs = 0;
    this._calls = 0;
    this._triangles = 0;
  }

  static override styles = css`
    :host {
      display: block;
      font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
      font-size: 11px;
      line-height: 1.35;
      color: #e9ffe9;
      background: rgba(0, 0, 0, 0.55);
      border-radius: 4px;
      padding: 4px 8px;
      pointer-events: none;
      user-select: none;
      z-index: 99999;
      min-width: 64px;
      text-align: left;
      backdrop-filter: blur(2px);
      -webkit-backdrop-filter: blur(2px);
    }
    :host([position="top-right"]),
    :host([position="top-left"]),
    :host([position="bottom-right"]),
    :host([position="bottom-left"]) {
      position: fixed;
      margin: 6px;
    }
    :host([position="top-right"]) {
      top: 0;
      right: 0;
    }
    :host([position="top-left"]) {
      top: 0;
      left: 0;
    }
    :host([position="bottom-right"]) {
      bottom: 0;
      right: 0;
    }
    :host([position="bottom-left"]) {
      bottom: 0;
      left: 0;
    }
    :host([hidden]) {
      display: none;
    }
    .row {
      display: flex;
      justify-content: space-between;
      gap: 8px;
    }
    .label {
      color: #9fe89f;
      opacity: 0.8;
    }
    .value {
      font-variant-numeric: tabular-nums;
      font-weight: 600;
    }
    .warn {
      color: #ffd37a;
    }
    .bad {
      color: #ff8a8a;
    }
  `;

  override connectedCallback(): void {
    super.connectedCallback();
    if (!this.hasAttribute("position")) {
      this.setAttribute("position", this.position);
    }
    this._startLoop();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this._stopLoop();
  }

  override updated(changed: Map<string, unknown>): void {
    if (changed.has("position")) {
      // Reflect prop to attribute so :host([position="..."]) selectors match
      // when consumers assign it imperatively rather than via markup.
      if (this.getAttribute("position") !== this.position) {
        this.setAttribute("position", this.position);
      }
    }
  }

  private _startLoop(): void {
    if (this._rafId !== null) return;
    this._lastFrameTime = performance.now();
    this._lastDisplayTime = this._lastFrameTime;
    const tick = (now: number) => {
      this._rafId = requestAnimationFrame(tick);
      const delta = now - this._lastFrameTime;
      this._lastFrameTime = now;
      if (delta > 0 && delta < 1000) {
        this._samples.push(delta);
        if (this._samples.length > this._sampleCap) this._samples.shift();
      }
      if (now - this._lastDisplayTime >= this.updateInterval) {
        this._lastDisplayTime = now;
        this._flush();
      }
    };
    this._rafId = requestAnimationFrame(tick);
  }

  private _stopLoop(): void {
    if (this._rafId !== null) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
  }

  private _flush(): void {
    if (this._samples.length === 0) return;
    let sum = 0;
    for (const s of this._samples) sum += s;
    const avg = sum / this._samples.length;
    this._frameMs = avg;
    this._fps = avg > 0 ? 1000 / avg : 0;

    if (this.detailed) {
      const stats = this._tryReadEngineStats();
      if (stats) {
        this._calls = stats.calls;
        this._triangles = stats.triangles;
      }
    }
  }

  private _tryReadEngineStats():
    | { calls: number; triangles: number }
    | null {
    try {
      const w = window as unknown as {
        Engine?: { activeEngine?: unknown };
      };
      const eng = w.Engine?.activeEngine as
        | {
            getLastWebGLMainFrameStats?: () => {
              calls: number;
              triangles: number;
            } | null;
          }
        | undefined;
      const stats = eng?.getLastWebGLMainFrameStats?.();
      return stats ?? null;
    } catch {
      return null;
    }
  }

  private _fpsClass(): string {
    if (this._fps === 0) return "";
    if (this._fps < 24) return "bad";
    if (this._fps < 50) return "warn";
    return "";
  }

  override render() {
    const fpsClass = this._fpsClass();
    return html`
      <div class="row">
        <span class="label">FPS</span>
        <span class="value ${fpsClass}"
          >${this._fps ? this._fps.toFixed(1) : "--"}</span
        >
      </div>
      ${this.detailed
        ? html`
            <div class="row">
              <span class="label">ms</span>
              <span class="value"
                >${this._frameMs ? this._frameMs.toFixed(2) : "--"}</span
              >
            </div>
            ${this._calls || this._triangles
              ? html`
                  <div class="row">
                    <span class="label">calls</span>
                    <span class="value">${this._calls}</span>
                  </div>
                  <div class="row">
                    <span class="label">tris</span>
                    <span class="value"
                      >${this._formatCount(this._triangles)}</span
                    >
                  </div>
                `
              : null}
          `
        : null}
    `;
  }

  private _formatCount(n: number): string {
    if (n >= 1_000_000) return (n / 1_000_000).toFixed(2) + "M";
    if (n >= 1_000) return (n / 1_000).toFixed(1) + "k";
    return String(n);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "sugar-fps-monitor": SugarFpsMonitor;
  }
}
